import React from 'react';

export type EventAnalyticsTab = 'coupons' | 'live_events';

interface EventAnalyticsDashboardHeaderProps {
    activeTab: EventAnalyticsTab;
    onTabChange: (tab: EventAnalyticsTab) => void;
}

// Coupon/tag outline icon (16x16) – per Figma
const CouponIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
        <path
            d="M3 2h10v4.67a1.33 1.33 0 0 1-.67 1.15L8 10.33 3.67 7.82A1.33 1.33 0 0 1 3 6.67V2z"
            stroke="currentColor"
            strokeWidth="1.35"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path d="M6.67 2v8.33M3 5.33h7.33" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" />
    </svg>
);

// Live/activity wave outline icon (16x16) – per Figma
const LiveEventsIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
        <path
            d="M2 8h2l2-4 2 8 2-4h2"
            stroke="currentColor"
            strokeWidth="1.33"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

/** Pill tab switcher only – use next to Coupon header when add-on is on so user can switch to Live Events. */
export const CouponLiveEventTabSwitcher: React.FC<{
    activeTab: EventAnalyticsTab;
    onTabChange: (tab: EventAnalyticsTab) => void;
}> = ({ activeTab, onTabChange }) => (
    <div
        className="shrink-0 pb-[4px]"
        style={{
            paddingBottom: '4px',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            display: 'inline-flex',
        }}
    >
        <div
            className="flex rounded-[24px] overflow-hidden bg-[#F2FCFA] dark:bg-gray-800 border border-[rgba(0,196,154,0.20)] dark:border-gray-600"
            style={{
                padding: '4.80px',
                borderRadius: '24px',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                gap: 0,
                display: 'inline-flex',
            }}
        >
            <button
                type="button"
                onClick={() => onTabChange('coupons')}
                className="min-w-[160px] rounded-[24px] flex items-center justify-center gap-[8px] border-0 cursor-pointer transition-colors"
                style={{
                    paddingLeft: '32.40px',
                    paddingRight: '32.40px',
                    paddingTop: '4px',
                    paddingBottom: '4px',
                    background: activeTab === 'coupons' ? '#00D6A8' : 'transparent',
                    borderRadius: '24px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '8px',
                    display: 'flex',
                }}
            >
                <span className={activeTab === 'coupons' ? 'text-[#0A0A0A]' : 'text-[#0A0A0A] dark:text-gray-300'}>
                    <CouponIcon />
                </span>
                <span
                    className={`text-center ${activeTab === 'coupons' ? 'text-[#0A0A0A]' : 'text-[#0A0A0A] dark:text-gray-300'}`}
                    style={{
                        justifyContent: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        fontSize: '14px',
                        fontFamily: 'Nunito',
                        fontWeight: 500,
                        lineHeight: '20px',
                        wordWrap: 'break-word',
                    }}
                >
                    Coupons
                </span>
            </button>
            <button
                type="button"
                onClick={() => onTabChange('live_events')}
                className="min-w-[160px] rounded-[24px] flex items-center justify-center gap-[8px] border-0 cursor-pointer transition-colors"
                style={{
                    paddingLeft: '32.40px',
                    paddingRight: '32.40px',
                    paddingTop: '4px',
                    paddingBottom: '4px',
                    background: activeTab === 'live_events' ? '#00D6A8' : 'transparent',
                    borderRadius: '24px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '8px',
                    display: 'flex',
                }}
            >
                <span className={activeTab === 'live_events' ? 'text-[#0A0A0A]' : 'text-[#0A0A0A] dark:text-gray-300'}>
                    <LiveEventsIcon />
                </span>
                <span
                    className={`text-center ${activeTab === 'live_events' ? 'text-[#0A0A0A]' : 'text-[#0A0A0A] dark:text-gray-300'}`}
                    style={{
                        justifyContent: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        fontSize: '14px',
                        fontFamily: 'Nunito',
                        fontWeight: 500,
                        lineHeight: '20px',
                        wordWrap: 'break-word',
                    }}
                >
                    Live Events
                </span>
            </button>
        </div>
    </div>
);

export const EventAnalyticsDashboardHeader: React.FC<EventAnalyticsDashboardHeaderProps> = ({
    activeTab,
    onTabChange,
}) => {
    return (
        <div className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between gap-[6.5px]">
            {/* Left: Title + Subtitle (left-aligned) */}
            <div className="flex-1 min-w-0 flex flex-col gap-[9.75px]">
                <h1
                    className="text-[#364153] dark:text-white text-left"
                    style={{
                        fontSize: '29.30px',
                        fontFamily: 'Nunito',
                        fontWeight: 700,
                        lineHeight: '32.50px',
                        wordWrap: 'break-word',
                    }}
                >
                    Event Analytics Dashboard
                </h1>
                <p
                    className="text-[#4A5565] dark:text-gray-400 text-left"
                    style={{
                        fontSize: '14.60px',
                        fontFamily: 'Nunito',
                        fontWeight: 400,
                        lineHeight: '22.75px',
                        wordWrap: 'break-word',
                    }}
                >
                    Track event performance, customer engagement, and geographic insights
                </p>
            </div>

            {/* Right: Tab switcher pill */}
            <CouponLiveEventTabSwitcher activeTab={activeTab} onTabChange={onTabChange} />
        </div>
    );
};
